import React from 'react';
import {
  Button, Select, EmptyState, ErrorState, Alert, Badge, IconButton, Checkbox,
  Dialog, DialogContent, DialogHeader, DialogTitle, ConfirmDialog,
} from '@parsina/framework';
import {
  FolderOpen, Upload, FileText, ChevronDown, Trash2, Image as ImageIcon,
  AlertTriangle, ScanText, Sparkles, PencilRuler, Eye, ExternalLink,
} from 'lucide-react';
import { C, CT } from '../../../constants/theme';
import { LTR_TEXT, useTranslation } from '../../../i18n/I18nProvider.jsx';
import { bffQuery } from '../../../lib/bffClient.js';
import ImageWorkspace from '../../../components/ImageWorkspace/ImageWorkspace.jsx';
import { FilesPaneSkeleton } from '../Visit.skeleton.jsx';
import { isRenderableImage } from '../../../lib/imageMarks.js';
import {
  fileToBase64, MAX_UPLOAD_BYTES, DOCUMENT_TYPES, VIEW_TYPES, isImageType,
  canExtractReport, fileSizeParts, VISIT_FILE_URL_QUERY, labelFor,
} from '../Visit.shared.js';

// §3.2.5 — فایل‌ها.
//
//   "Display uploaded documents (lab results, images)."
//   "Report Association: Clearly indicate which extracted report belongs to
//    which uploaded file."
//   "Prioritize clinically relevant and recent files."
//
// ── UPLOAD IS PLAIN, AND THAT IS THE CHANGE (2026-08-31) ───────────────────
// This tab used to carry a pre-ticked «استخراج گزارش» checkbox, and the
// predicate behind it counted every image/jpeg as extractable — so uploading a
// periapical sent it to a model asked to transcribe text a radiograph does not
// contain. It came back empty every time, the card said «گزارشی استخراج نشده»,
// and the dentist had waited for that answer while a credit warning was on
// screen for a call that (see the BFF) was never actually billed.
//
// Upload now stores bytes. Afterwards, per file, on a control the dentist
// presses while looking at the thing:
//
//   «استخراج متن»  — a document has TEXT in it. PDFs, photographed lab sheets.
//   «خواندن تصویر» — a radiograph has FINDINGS in it, and they have a place.
//
// Those are two different questions, and the old tab asked the first one about
// everything.
//
// ── The association is a STORED FACT, not a layout ─────────────────────────
// A file, its extracted report and its marks arrive on ONE row from the BFF,
// because three services wrote three rows keyed by the same
// `storage:labfile:<id>` reference. Nothing here pairs anything by timestamp —
// a tab that guessed would eventually pair the wrong two, and would look right
// while doing it.
//
// ── The bytes are fetched ONE AT A TIME ────────────────────────────────────
// A data: URI costs the file's size × 1.33 in the response, so `visitFileUrl`
// is a separate query fired when a file is opened. A files list must not
// download every attachment to draw a list of names.

// What the picker will admit.
//
// ENUMERATED, never the image wildcard. The wildcard admits HEIC — which an
// iPhone produces by default and which no browser but Safari draws — so such a
// file uploads perfectly and then shows a blank frame with no error anywhere.
// The document list includes the photo types too, because a lab sheet snapped
// with a phone is the common case in an Iranian clinic.
//
// They are constants rather than inline strings for a second reason that is not
// style: tests/mobileComposition.test.js scans this SOURCE and strips block
// comments before looking, so a slash and a star written adjacently anywhere in
// this file — which is what the wildcard form is — reads to it as a comment
// opener and silently swallows the rest of the element. Keeping that sequence
// out of the file is what keeps the guard able to see it.
const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/bmp,image/tiff';
const DOCUMENT_ACCEPT = `application/pdf,${IMAGE_ACCEPT}`;

/** One file's bytes, fetched once and remembered for as long as it is open. */
function useFileUrl(patientId, file) {
  const [url, setUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const fileId = file?.id;

  React.useEffect(() => {
    if (!fileId || !patientId) return undefined;
    let alive = true;
    setUrl(null);
    setFailed(false);
    setLoading(true);
    bffQuery(VISIT_FILE_URL_QUERY, { patientId, fileId })
      .then((data) => {
        if (!alive) return;
        if (data?.visitFileUrl) setUrl(data.visitFileUrl);
        else setFailed(true);
      })
      .catch(() => { if (alive) setFailed(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [patientId, fileId]);

  return { url, loading, failed };
}

function FileCard({
  f, t, formatNumber, formatDate, editable, busy,
  onDelete, onOpen, onOpenExternal, onRunOcr,
}) {
  const [open, setOpen] = React.useState(false);
  const bodyId = React.useId();

  const size = fileSizeParts(f.fileSize);
  const isImage = isImageType(f.fileType);
  /**
   * Can this file be OPENED at all?
   *
   * Only two things matter: the bytes still exist, and this app can draw them.
   * NOT whether it is a registered dental image — that gate was here until it
   * turned out to hide the preview on every file uploaded before the workspace
   * existed, which is most of them, and on any file whose link write failed.
   * Looking at a radiograph is the least a files tab owes its reader; the view
   * type only decides whether it can also be drawn on and read.
   */
  const viewable = isImage && !!f.storageFileId && isRenderableImage(f.fileType);
  const extractable = canExtractReport(f.fileType, f.viewType);

  return (
    <div style={{
      border: `1px solid ${C.border}`, borderRadius: 10,
      background: CT.surface, marginBottom: 8, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px',
        flexWrap: 'wrap',
      }}>
        {isImage
          ? <ImageIcon size={16} color={C.teal} aria-hidden />
          : <FileText size={16} color={C.teal} aria-hidden />}

        <div style={{ flex: 1, minWidth: 140 }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: C.textPrimary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {f.fileName}
          </div>
          <div style={{ fontSize: 11, color: C.textMuted }}>
            {f.createdAt ? formatDate(f.createdAt, 'numeric') : ''}
            {size ? ` · ${formatNumber(size.value)} ${t(`visit.files.units.${size.unit}`)}` : ''}
            {f.viewType
              ? ` · ${labelFor(t, 'visit.files.viewTypes', f.viewType)}`
              : (f.documentType ? ` · ${labelFor(t, 'visit.files.docTypes', f.documentType)}` : '')}
          </div>
        </div>

        {f.fromThisVisit && <Badge variant="outline">{t('visit.files.thisVisit')}</Badge>}

        {/* What is already ON this file, said before it is opened — the reason
            to open one rather than another. */}
        {f.annotations?.length > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            fontSize: 11, color: C.textSecondary,
          }}>
            <PencilRuler size={12} aria-hidden />
            {formatNumber(f.annotations.length)}
          </span>
        )}
        {f.aiFindings?.length > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            fontSize: 11, color: CT.accentInk,
          }}>
            <Sparkles size={12} aria-hidden />
            {formatNumber(f.aiFindings.length)}
          </span>
        )}

        {f.teethFdi.length > 0 && (
          <span style={{ fontSize: 11, color: C.textSecondary }} {...LTR_TEXT}>
            {f.teethFdi.join(', ')}
          </span>
        )}

        {viewable && (
          <Button variant="outline" className="min-h-11 flex-none" onClick={() => onOpen(f)}>
            <Eye size={13} aria-hidden style={{ marginInlineEnd: 4 }} />
            {t('visit.files.openImage')}
          </Button>
        )}

        {/* A file this app cannot draw — a PDF, a HEIC from an iPhone — still
            has to be openable, and the browser can show both. `download` is
            deliberately absent: a dentist checking a referral letter wants to
            look at it, not to collect it. */}
        {!viewable && f.storageFileId && (
          <Button
            variant="outline"
            className="min-h-11 flex-none"
            onClick={() => onOpenExternal(f)}
            disabled={busy}
          >
            <ExternalLink size={13} aria-hidden style={{ marginInlineEnd: 4 }} />
            {t('visit.files.openFile')}
          </Button>
        )}

        {/* «استخراج متن» — offered only where there is text to find. A
            radiograph has none; asking anyway is what this rework removed. */}
        {editable && extractable && !f.reportText && (
          <Button
            variant="outline"
            className="min-h-11 flex-none"
            onClick={() => onRunOcr(f)}
            disabled={busy}
          >
            <ScanText size={13} aria-hidden style={{ marginInlineEnd: 4 }} />
            {busy ? t('visit.files.extracting') : t('visit.files.extract')}
          </Button>
        )}

        {/* The report disclosure — §3.2.5's "clearly indicate which extracted
            report belongs to which uploaded file", answered by putting the
            report inside the file's own card. */}
        {(f.reportText || f.abnormalFlags.length > 0) && (
          <Button
            variant="outline"
            className="min-h-11 flex-none"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen((o) => !o)}
          >
            {t('visit.files.hasReport')}
            <ChevronDown
              size={13}
              aria-hidden
              style={{
                marginInlineStart: 4,
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform .15s',
              }}
            />
          </Button>
        )}

        {editable && (
          <IconButton
            variant="ghost"
            size={44}
            icon={<Trash2 size={14} />}
            label={t('visit.files.remove', { name: f.fileName })}
            onClick={() => onDelete(f.id)}
          />
        )}
      </div>

      {open && (
        <div id={bodyId} style={{
          borderTop: `1px solid ${C.border}`, background: CT.surface2, padding: '10px 12px',
        }}>
          {f.abnormalFlags.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
              fontSize: 12, color: CT.warnInk, fontWeight: 700,
            }}>
              <AlertTriangle size={13} aria-hidden />
              {f.abnormalFlags.join(' · ')}
            </div>
          )}
          {f.reportText ? (
            <div style={{
              fontSize: 12.5, color: C.textSecondary, lineHeight: 1.9,
              whiteSpace: 'pre-wrap',
            }}>
              {f.reportText}
            </div>
          ) : (
            // "No report" and "an empty report" are different facts, and only
            // one of them means the extraction was never run.
            <div style={{ fontSize: 12, color: C.textMuted }}>
              {t('visit.files.noReport')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FilesPane({
  files, visit, editable, selectedTooth, compact = false, onConfirmFinding,
}) {
  const { t, formatNumber, formatDate } = useTranslation();
  const {
    files: rows, loading, error, uploading, busyFileId,
    upload, remove, runOcr, analyze, saveAnnotations, register, reload,
  } = files;

  const [kind, setKind] = React.useState('image');
  const [viewType, setViewType] = React.useState('periapical');
  const [documentType, setDocumentType] = React.useState('lab_report');
  const [attachTooth, setAttachTooth] = React.useState(true);
  const [failure, setFailure] = React.useState(null);
  const [openId, setOpenId] = React.useState(null);
  const [fetchingId, setFetchingId] = React.useState(null);
  const [dirty, setDirty] = React.useState(false);
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  // How each film is being looked at, KEYED BY FILE and held here rather than
  // in the workspace: the modal unmounts every time it closes — confirming a
  // finding closes it and sends the dentist to the tooth editor — and they come
  // back to the same image for the next finding. Re-zooming and re-pushing the
  // contrast each time would make the second finding cost more than the first.
  const [views, setViews] = React.useState({});
  const inputRef = React.useRef(null);

  const patientId = visit?.patientId;

  // The open file is looked up FRESH from the list on every render rather than
  // held in state: the list refetches after a save or a read, and a held copy
  // would keep showing the marks and findings from before it.
  const openFile = rows.find((r) => r.id === openId) || null;
  const fileUrl = useFileUrl(patientId, openFile);

  const onPick = async (event) => {
    const file = event.target.files?.[0];
    // Reset immediately, so picking the SAME file twice still fires a change.
    event.target.value = '';
    if (!file) return;

    setFailure(null);
    if (file.size > MAX_UPLOAD_BYTES) {
      // Refused here as well as server-side: sending 20 MB to be told no is a
      // slow way to learn it, especially on a clinic's uplink.
      setFailure(new Error(t('visit.files.tooLarge')));
      return;
    }

    // HEIC is admitted by `accept="image/*"`, is what an iPhone produces by
    // default, and is drawn by no browser but Safari — so it uploads fine and
    // then shows a blank frame with no error anywhere. Refusing at the picker
    // is the only place this is legible.
    const asImage = kind === 'image';
    if (asImage && !isRenderableImage(file.type)) {
      setFailure(new Error(t('visit.files.unsupportedImage')));
      return;
    }

    let base64;
    try {
      base64 = await fileToBase64(file);
    } catch {
      setFailure(new Error(t('visit.files.readFailed')));
      return;
    }

    const err = await upload({
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      fileData: base64,
      // A view type is what REGISTERS the upload as a dental image. A document
      // gets a documentType instead, and neither gets both: a PDF referral with
      // a view type would be handed to the image reader as if it were a film.
      viewType: asImage ? viewType : null,
      documentType: asImage ? null : documentType,
      teethFdi: asImage && attachTooth && selectedTooth ? [selectedTooth] : [],
    });
    if (err) setFailure(err);
  };

  const viewOf = (id) => views[id] || { zoom: 1, brightness: 100, contrast: 100 };

  const closeNow = () => { setOpenId(null); setDirty(false); };

  /** Close, unless there are marks that exist nowhere else yet. */
  const requestClose = () => {
    if (dirty) setConfirmDiscard(true);
    else closeNow();
  };

  /**
   * A finding was ticked. Close the modal and hand it up.
   *
   * CLOSING IS THE POINT, not a limitation. `ToothDrawer` is built on the kit's
   * `Drawer`, which is `position: absolute` at z-index 11; `Dialog` is `fixed`
   * at 1100 (the scale is written at the top of the kit's Dialog.jsx). So a
   * drawer opened while this modal is up would render UNDERNEATH it — the
   * dentist would tick a finding and watch nothing happen.
   *
   * The alternatives were worse: raising the drawer means editing a kit
   * component three apps share, and rendering a second tooth editor inside the
   * modal means two editors that must agree about a clinical write.
   *
   * And it reads correctly anyway — confirming a finding IS the move from
   * reading the film to charting the tooth. The zoom and contrast survive in
   * `views`, so coming back for the next finding costs one click.
   */
  const confirmFinding = (finding) => {
    closeNow();
    onConfirmFinding?.(finding);
  };

  const drop = async (fileId) => {
    setFailure(null);
    if (fileId === openId) setOpenId(null);
    const err = await remove(fileId);
    if (err) setFailure(err);
  };

  /**
   * Open a file this app cannot draw — a PDF, a scan in a format no browser
   * renders inline — in a new tab.
   *
   * The bytes arrive as a `data:` URI, so this fetches them on demand rather
   * than holding one per row: a files list must not download every attachment
   * to draw a list of names. A blob URL and not the data URI itself, because
   * Chrome refuses top-level navigation to `data:` — the tab would open blank
   * with nothing anywhere saying why.
   */
  const openExternal = async (file) => {
    setFailure(null);
    setFetchingId(file.id);
    try {
      const data = await bffQuery(VISIT_FILE_URL_QUERY, { patientId, fileId: file.id });
      if (!data?.visitFileUrl) {
        setFailure(new Error(t('visit.files.previewFailed')));
        return;
      }
      const res = await fetch(data.visitFileUrl);
      const url = URL.createObjectURL(await res.blob());
      window.open(url, '_blank', 'noopener');
      // Released on the next tick rather than immediately: revoking before the
      // new tab has read it leaves that tab blank.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setFailure(new Error(t('visit.files.previewFailed')));
    } finally {
      setFetchingId(null);
    }
  };

  // The pane's own shape — the upload form and the file cards — never a
  // centred spinner (DESIGN.md §"Loading & skeleton").
  if (loading) {
    return <FilesPaneSkeleton label={t('common.loading')} />;
  }

  if (error) {
    return (
      <div role="alert">
        <ErrorState
          title={t('visit.files.error.title')}
          description={t('visit.files.error.body')}
          onRetry={reload}
          retryLabel={t('common.actions.retry')}
        />
      </div>
    );
  }

  return (
    <div data-testid="visit-files" data-state={rows.length ? 'content' : 'empty'}>
      {failure && (
        <Alert variant="error" style={{ marginBottom: 12 }}>
          {failure.message || t('visit.files.uploadFailed')}
        </Alert>
      )}

      {editable ? (
        <div style={{
          border: `1px solid ${C.border}`, borderRadius: 10,
          padding: 12, marginBottom: 14, background: CT.surface2,
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10,
          }}>
            {/* IMAGE or DOCUMENT, chosen first, because it decides everything
                else: an image gets a view type and a canvas, a document gets a
                type and an extract button. */}
            <Select
              label={t('visit.files.kind')}
              aria-label={t('visit.files.kind')}
              placeholder=""
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              options={[
                { value: 'image', label: t('visit.files.kinds.image') },
                { value: 'document', label: t('visit.files.kinds.document') },
              ]}
            />
            {kind === 'image' ? (
              <Select
                label={t('visit.files.viewType')}
                aria-label={t('visit.files.viewType')}
                placeholder=""
                value={viewType}
                onChange={(e) => setViewType(e.target.value)}
                options={VIEW_TYPES.map((v) => ({
                  value: v, label: t(`visit.files.viewTypes.${v}`),
                }))}
              />
            ) : (
              <Select
                label={t('visit.files.docType')}
                aria-label={t('visit.files.docType')}
                placeholder=""
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                options={DOCUMENT_TYPES.map((d) => ({
                  value: d, label: t(`visit.files.docTypes.${d}`),
                }))}
              />
            )}
          </div>

          {kind === 'image' && selectedTooth && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 10 }}>
              <Checkbox
                checked={attachTooth}
                onChange={setAttachTooth}
                label={t('visit.files.attachTooth', { fdi: formatNumber(selectedTooth) })}
                className="min-h-11"
              />
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            {/* The real control is the hidden input; the Button is what the
                dentist sees, so the target is a proper button rather than a
                browser-styled file field nobody can size. */}
            <input
              ref={inputRef}
              type="file"
              onChange={onPick}
              accept={kind === 'image' ? IMAGE_ACCEPT : DOCUMENT_ACCEPT}
              style={{ display: 'none' }}
              aria-hidden="true"
              tabIndex={-1}
            />
            <Button className="min-h-11" onClick={() => inputRef.current?.click()} disabled={uploading}>
              <Upload size={14} aria-hidden style={{ marginInlineEnd: 5 }} />
              {uploading ? t('visit.files.uploading') : t('visit.files.upload')}
            </Button>
          </div>

          {/* No credit warning here, and that is deliberate: uploading spends
              nothing now. The warning belongs on the two buttons that do. */}
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>
            {t('visit.files.plainUploadNote')}
          </div>
        </div>
      ) : (
        <Alert variant="info" style={{ marginBottom: 12 }}>{t('visit.readOnly.note')}</Alert>
      )}

      {rows.length === 0 ? (
        <EmptyState
          icon={<FolderOpen size={40} color={C.textMuted} />}
          title={t('visit.files.empty.title')}
          description={t('visit.files.empty.description')}
        />
      ) : (
        rows.map((f) => (
          <FileCard
            key={f.id}
            f={f}
            t={t}
            formatNumber={formatNumber}
            formatDate={formatDate}
            editable={editable}
            busy={busyFileId === f.id || fetchingId === f.id}
            onDelete={drop}
            onOpen={(file) => setOpenId(file.id)}
            onOpenExternal={openExternal}
            onRunOcr={async (file) => {
              setFailure(null);
              const err = await runOcr(file.id, file.documentType);
              if (err) setFailure(err);
            }}
          />
        ))
      )}

      {/* ── One image, opened ────────────────────────────────────────────
          A MODAL, not a replacement of this list. Reading a radiograph wants
          more than the half-width column this pane occupies on a desktop, and
          the list stays mounted underneath — so closing returns to exactly the
          scroll position the dentist left, and comparing two films is two
          clicks rather than a round trip that loses the zoom.

          `Dialog` on BOTH surfaces rather than a BottomSheet on the phone: the
          content is one dense thing — toolbar, canvas, findings — and a second
          shell would be a second layout to keep in step with it. The width is
          responsive instead. */}
      <Dialog
        open={!!openFile}
        onOpenChange={(next) => { if (!next) requestClose(); }}
      >
        {/* No `onEscapeKeyDown` / `onPointerDownOutside` guards on the content
            below, on purpose. `open` is CONTROLLED by `openFile`, so EVERY
            dismissal — the ✕, Esc, a backdrop click — arrives here as
            `onOpenChange(false)`, and `requestClose` is the single place that
            decides. Calling `preventDefault` on those two events instead blocks
            the close and asks nothing, which is how the dialog briefly became
            impossible to shut once it had unsaved marks in it. */}
        {openFile && (
          <DialogContent className="max-w-[min(1180px,96vw)]">
            <DialogHeader>
              <DialogTitle>{openFile.fileName}</DialogTitle>
            </DialogHeader>
            <ImageWorkspace
              file={openFile}
              url={fileUrl.url}
              loadingUrl={fileUrl.loading}
              urlFailed={fileUrl.failed}
              editable={editable}
              busy={busyFileId === openFile.id}
              compact={compact}
              view={viewOf(openFile.id)}
              onViewChange={(v) => setViews((all) => ({ ...all, [openFile.id]: v }))}
              onDirtyChange={setDirty}
              onBack={requestClose}
              onSave={(marks) => saveAnnotations(openFile.id, marks)}
              onAnalyze={() => analyze(openFile.id)}
              onRegister={(viewType) => register(openFile.id, viewType, openFile.teethFdi)}
              onConfirmFinding={confirmFinding}
              t={t}
              formatDate={formatDate}
              formatNumber={formatNumber}
            />
          </DialogContent>
        )}
      </Dialog>

      {/* Esc and a backdrop click are easy to hit by accident, and the marks
          drawn since the last ذخیره have no other copy. The inline version got
          away without this because leaving was a deliberate button press. */}
      <ConfirmDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title={t('visit.image.discard.title')}
        description={t('visit.image.discard.body')}
        confirmLabel={t('visit.image.discard.confirm')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        onConfirm={() => { setConfirmDiscard(false); closeNow(); }}
      />
    </div>
  );
}
